import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Form, Select, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { formatMoney } from '../../lib/money';
import { STREAMS, setStream, type SalesGroup, type Stream } from './api';

const { Text } = Typography;

export interface StreamMapDrawerProps {
  /** The group row to move; null closes the drawer. */
  group: (SalesGroup & { itemGroup: string }) | null;
  year: number;
  onClose: () => void;
}

/**
 * Management's one write on Insights: which stream an item group counts in.
 * Says what moves before it is saved (CLAUDE.md §10). Figures follow at once
 * because streams are applied when the figures are read, not stored.
 */
export function StreamMapDrawer({ group, year, onClose }: StreamMapDrawerProps): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<Stream | undefined>(undefined);

  const close = (): void => {
    setTarget(undefined);
    onClose();
  };

  const save = useMutation({
    mutationFn: (input: { itemGroup: string; stream: Stream }) => setStream(input.itemGroup, input.stream),
    onSuccess: (saved) => {
      void message.success(
        t('insights.map.saved', { group: saved.itemGroup, stream: t(`insights.stream.${saved.stream}`) }),
      );
      void queryClient.invalidateQueries({ queryKey: ['analytics'] });
      close();
    },
  });

  if (group === null) return null;

  const from = t(`insights.stream.${group.stream}`);
  const params = { amount: formatMoney(group.revenue), year: String(year), group: group.itemGroup, from };

  return (
    <ActionDrawer
      open
      title={t('insights.map.title', { group: group.itemGroup })}
      submitLabel={
        target === undefined
          ? t('insights.map.submitPending')
          : t('insights.map.submit', { stream: t(`insights.stream.${target}`) })
      }
      submitDisabled={target === undefined}
      submitting={save.isPending}
      onSubmit={() => {
        if (target !== undefined) save.mutate({ itemGroup: group.itemGroup, stream: target });
      }}
      onClose={() => {
        save.reset();
        close();
      }}
      consequence={
        target === undefined
          ? t('insights.map.consequencePending', params)
          : t('insights.map.consequence', { ...params, to: t(`insights.stream.${target}`) })
      }
      error={
        save.error === null ? undefined : (
          <Alert
            type="error"
            showIcon
            message={save.error instanceof ApiError ? save.error.message : t('insights.map.failed')}
          />
        )
      }
    >
      <Form layout="vertical">
        <Form.Item label={t('insights.map.countsIn')}>
          <Text strong>{from}</Text>
        </Form.Item>
        <Form.Item label={t('insights.map.moveTo')}>
          <Select<Stream>
            size="large"
            placeholder={t('insights.map.pick')}
            value={target}
            onChange={setTarget}
            options={STREAMS.filter((s) => s !== group.stream).map((s) => ({
              value: s,
              label: t(`insights.stream.${s}`),
            }))}
          />
        </Form.Item>
      </Form>
    </ActionDrawer>
  );
}
